import { HTTPException } from '../common/types/api'
import {
  ConfigTestReport,
  FetchTrainingDataParams,
  RequirementUploadParams,
  RequirementVersion,
  Requirements,
  Rules,
  RunTestsParams,
  TrainingData,
  TrainingDataCsvDownloadParams,
  TrainingDataForMatchingCriteria,
} from '../features/ConfigureDataCollectionAdminPanelPage/types'
import {
  PatternXrayTestBody,
  RuleIn,
  XrayPatternTestCaseResult,
} from '../features/ConfigureDataCollectionAdvancedAdminPanelPage/types'
import type { UpdateTrackingPolicyPayload } from '../features/ConfigureDataCollectionAdminPanelPage/types'
import { apiSlice } from './apiSlice'
import { WEB_APP_URL_CONFIG_STUDIO_API } from './configs'

export const apiSliceConfigStudioApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    validateDataCollectionRules: build.mutation<null | HTTPException, { rules: RuleIn[] }>({
      query: (body) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/config-management/validate-rules`,
        method: 'POST',
        body,
      }),
    }),

    // ** Config Studio ** //
    // Db Configs: What is deployed to DB (waiting for prod deployment).
    getDataCollectionConfigs: build.query<Rules, void>({
      query: () => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/config-management/download-configs`,
      }),
      providesTags: ['CompanyConfigFileDB'],
    }),
    updateTrackingPolicy: build.mutation<unknown, UpdateTrackingPolicyPayload>({
      query: (body) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/config-management/tracking-policy`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CompanyConfigFile', 'CompanyConfigFileDB'],
    }),
    uploadCompanyDbConfigs: build.mutation<RequirementVersion, { rules: RuleIn[] }>({
      query: (body) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/config-management/upload-rules`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CompanyConfigFileDB'],
    }),
    uploadRequirements: build.mutation<RequirementVersion | HTTPException, RequirementUploadParams>(
      {
        query: (body) => ({
          url: `${WEB_APP_URL_CONFIG_STUDIO_API}/requirement-management/upload-requirements`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['ActiveRequirementFile'],
      },
    ),
    getTestCases: build.query<Requirements, void>({
      query: () => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/requirement-management/download-requirements`,
      }),
      providesTags: ['ActiveRequirementFile'],
    }),
    getConfigTestReport: build.query<ConfigTestReport, void>({
      query: () => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/config-management/config-file-upload-report`,
      }),
      providesTags: ['CompanyConfigFileTestReport'],
    }),
    runXRayPatternTests: build.mutation<XrayPatternTestCaseResult[], PatternXrayTestBody>({
      query: (body) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/x-ray/x-ray-pattern-tests`,
        method: 'POST',
        body,
      }),
    }),
    getXrayZipFileList: build.query<string[], void>({
      query: () => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/x-ray/zip/list`,
      }),
      providesTags: ['xRayZipFile'],
    }),
    getXRayZipFileDownloadLink: build.query<string, { zip_name: string }>({
      query: (params) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/x-ray/zip`,
        params,
      }),
    }),
    uploadXrayZipFile: build.mutation<void, FormData>({
      query: (body) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/x-ray/zip`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['xRayZipFile'],
    }),
    getTemplateDataCollectionConfigs: build.query<Rules, void>({
      query: () => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/templates/configs`,
      }),
    }),
    getTemplateTestCases: build.query<Requirements, void>({
      query: () => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/templates/test-cases`,
      }),
    }),
    // ** Data collection configs ** //
    runConfigTests: build.mutation<ConfigTestReport, RunTestsParams>({
      query: (body) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/config-management/run-tests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CompanyConfigFileTestReport'],
    }),

    getTrainingData: build.query<TrainingData[], FetchTrainingDataParams>({
      query: (params) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/data-management/training-data`,
        params,
      }),
    }),
    getTrainingDataForMatchingCriteria: build.query<
      TrainingData[],
      TrainingDataForMatchingCriteria
    >({
      query: ({ body, ...params }) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/data-management/training-data/for-matching-criteria`,
        method: 'POST',
        params,
        body,
      }),
    }),
    // Mutation based download, because we don't want to cache the file response.
    downloadTrainingData: build.mutation<string, TrainingDataCsvDownloadParams>({
      query: (params) => ({
        url: `${WEB_APP_URL_CONFIG_STUDIO_API}/data-management/training-data-csv`,
        method: 'GET',
        params,
        responseHandler: 'text',
      }),
    }),
  }),
})

export const {
  useValidateDataCollectionRulesMutation,
  useGetDataCollectionConfigsQuery,
  useUpdateTrackingPolicyMutation,
  useUploadCompanyDbConfigsMutation,
  useUploadRequirementsMutation,
  useGetTestCasesQuery,
  useGetConfigTestReportQuery,
  useRunXRayPatternTestsMutation,
  useGetXrayZipFileListQuery,
  useLazyGetXRayZipFileDownloadLinkQuery,
  useUploadXrayZipFileMutation,
  useGetTemplateDataCollectionConfigsQuery,
  useGetTemplateTestCasesQuery,
  useRunConfigTestsMutation,
  useGetTrainingDataQuery,
  useGetTrainingDataForMatchingCriteriaQuery,
  useDownloadTrainingDataMutation,
} = apiSliceConfigStudioApi

